import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Clock, HelpCircle, History, ListChecks, MailPlus } from 'lucide-react';
import { KATEGORIE_TEXT, VORLAGEN, sichtbareFelder, sichtbareFragen, vorlage, type EmojiStufe } from '../../lib/mailBuilder/vorlagen';
import { BEISPIELE } from '../../lib/mailBuilder/beispiele';
import { renderMail } from '../../lib/mailBuilder/render';
import { sportschule, type Sportschule } from '../../lib/sportschulen';
import { kannVorbereiten } from '../../lib/rollen';
import { useAuthStore } from '../../stores/authStore';
import { PageHeader } from '../../components/PageHeader';
import { MailVorschau } from '../../components/MailVorschau/MailVorschau';
import { EmojiStufenWahl } from '../../components/EmojiStufenWahl';
import '../Assistent/Assistent.css';
import '../Assistent/MailAssistent.css';
import './Vorlagen.css';

export function Vorlagen() {
  const [params, setParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const v = vorlage(params.get('vorlage')) ?? VORLAGEN[0];
  const [stufe, setStufe] = useState<EmojiStufe>('normal');

  const beispiel = useMemo(() => {
    const b = BEISPIELE[v.id]?.() ?? { schulen: [], antworten: {}, daten: v.start({ schulen: [], kontaktEmail: '' }) };
    const schulen = b.schulen.map((n) => sportschule(n)).filter((x): x is Sportschule => !!x);
    const k = { schulen, kontaktEmail: schulen[0]?.email ?? 'backoffice@randori-pro.de' };
    const daten = { ...b.daten, emojis: stufe };
    return { html: renderMail(v.bauen(daten, k, b.antworten)), betreff: v.betreff(daten, k), antworten: b.antworten, daten };
  }, [v, stufe]);

  const fragen = sichtbareFragen(v, beispiel.antworten);
  const felder = sichtbareFelder(v, beispiel.daten, beispiel.antworten);

  return (
    <div>
      <PageHeader
        title="Vorlagen"
        subtitle="Acht Vorlagen, nachgebaut aus den Mails der letzten Monate. Links wählen, rechts sehen, wie die fertige Mail aussieht."
      />
      <div className="vl-layout">
        <div className="vl-liste" data-tour="vorlagen-galerie" role="listbox" aria-label="Vorlagen">
          {VORLAGEN.map((x) => (
            <button
              key={x.id}
              type="button"
              role="option"
              aria-selected={x.id === v.id}
              className={`auswahl-kachel art-kachel vl-kachel${x.id === v.id ? ' aktiv' : ''}`}
              onClick={() => setParams({ vorlage: x.id }, { replace: true })}
            >
              <span className="as-emoji" aria-hidden="true">{x.emoji}</span>
              <span className="art-text">
                <strong>{x.name}</strong>
                <span>{x.kurz}</span>
                <span className="as-kategorie">{KATEGORIE_TEXT[x.kategorie]}</span>
              </span>
            </button>
          ))}
        </div>

        <div className="vl-detail">
          <div className="vl-kopf">
            <div>
              <h2>{v.emoji} {v.name}</h2>
              <p>{v.wann}</p>
            </div>
            {kannVorbereiten(user) && (
              <Link to={`/neue-mail?vorlage=${v.id}`} className="btn btn-primary" data-tour="vorlagen-nutzen">
                <MailPlus size={16} aria-hidden="true" /> Diese Vorlage nutzen
              </Link>
            )}
          </div>

          <div className="vl-fakten">
            <p><Clock size={14} aria-hidden="true" /> <strong>Vorlauf:</strong> {v.vorlauf}</p>
            <p><History size={14} aria-hidden="true" /> <strong>Gebaut nach:</strong> {v.vorbild.join(' · ')}</p>
            <p><strong>Betreff zum Beispiel:</strong> {beispiel.betreff[0]}</p>
          </div>

          <div className="vl-stufen" data-tour="vorlagen-emojis">
            <EmojiStufenWahl id="assistent-vorlagen-emojis" wert={stufe} onChange={setStufe} />
            <span className="form-hint" style={{ margin: 0 }}>So sieht die Mail je nach Emoji-Stufe aus. Im Assistenten wählst Du sie unter „Gestaltung".</span>
          </div>

          <MailVorschau html={beispiel.html} titel="Beispiel mit Mustertext" tour="vorlagen-vorschau" />

          <div className="vl-spalten">
            <section>
              <h3><HelpCircle size={15} aria-hidden="true" /> Diese Fragen kommen vorab</h3>
              <ul className="vl-fragen">
                {fragen.map((f) => (
                  <li key={f.id}><strong>{f.frage}</strong><span>{f.warum}</span></li>
                ))}
              </ul>
            </section>
            <section>
              <h3><ListChecks size={15} aria-hidden="true" /> Das füllst Du aus</h3>
              <ul className="vl-felder">
                {felder.map((f) => (
                  <li key={f.id}>{f.label}{f.pflicht === true && <span className="as-pflicht" aria-label="Pflichtfeld"> *</span>}</li>
                ))}
              </ul>
              <p className="form-hint">Aufbau, Farben, Signatur, Hinweis- und Fußzeile setzt das Mailtool selbst.</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
